import { useCallback } from 'react'
import { qs } from 'utils/api'

import { useFetchApi } from './useFetchApi'

const DEFAULT_PAGE_SIZE = 100

interface PaginatedEnvelope<T> {
  data: T[]
}

/**
 * Returns a stable `(q) => Promise<T[]>` loader bound to a list endpoint.
 * Pairs naturally with AsyncCombobox / AsyncMultiCombobox — the server does
 * the search, the client only ever sees one page at a time. Unwraps the
 * `{ data, total, hasMore }` envelope every paginated list endpoint returns.
 */
export function useAsyncLoader<T>(
  endpoint: string,
  baseParams?: Record<string, unknown>,
  pageSize: number = DEFAULT_PAGE_SIZE,
): (q: string) => Promise<T[]> {
  const { fetchApi } = useFetchApi()
  return useCallback(
    async (q: string) => {
      const result = await fetchApi<PaginatedEnvelope<T>>(
        `${endpoint}${qs({ ...baseParams, take: pageSize, q: q || undefined })}`,
      )
      return result.data
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, pageSize, fetchApi, JSON.stringify(baseParams ?? {})],
  )
}
